
/// <amd-module name="Wit/Search/WitPeoplePicker" />
import React = require("react");
import * as Resources from "Wit/Search/Resources"
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { ISearchInput } from "VSS/Platform/Search";
import { IIdentity } from "VSSUI/Components/IdentityPickerDropdown/SharedIdentityPicker.Props"
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { SuggestionsList } from "VSSUI/SuggestionsList";
import { Observer } from "VSSUI/Observer";
import { IdentityPickerSuggestionItem } from "VSSUI/Components/IdentityPickerSuggestionsList/IdentityPickerSuggestionItem";
import { IIdentitySuggestionItemProps } from "VSSUI/Components/IdentityPickerSuggestionsList/IdentityPickerSuggestionsList.Props";
import { getSuggestedText } from "./FieldSuggestionStore";
import { KeyCode } from "VSSUI/Util";
import { TimerManagement } from "VSS/Core/TimerManagement";

const resultsMaximumNumber = 5;

export interface IWITIdentityPickerProps {
    searchInput: ISearchInput;

    searchText: string;
}

export interface IWITIdentityPickerstate {
    suggestions: IIdentity[];

    areSuggestionsVisible: boolean;

    openedIdentityCard: IIdentity | undefined;

    selectedIndex: number;

    suggestionsLoading: boolean;
}

export class WITIdentityPicker extends VssComponent<IWITIdentityPickerProps, IWITIdentityPickerstate> {
    private updateSuggestionsOnType: (suggestions: IIdentity[] | PromiseLike<IIdentity[]>) => void;
    private pickerProvider: PeoplePickerProvider;
    private readonly timerManagement:TimerManagement;

    constructor(props: IWITIdentityPickerProps, context: IVssComponentContext) {
        super(props);
        this.state = {
            suggestions: [],
            areSuggestionsVisible: true,
            openedIdentityCard: undefined,
            selectedIndex: 0,
            suggestionsLoading: true
        };

        this.pickerProvider = new PeoplePickerProvider(context.pageContext,
            {
                query: this.props.searchInput.queryText(),
                identityTypes: ["user"],
                operationScopes: ["ims", "source"]
            });

        this.timerManagement = new TimerManagement();
        this.updateSuggestionsOnType = this.timerManagement.debounce(this.updateSuggestions, 150);
    }


    public render(): JSX.Element {
        return (
            <Observer
                suggestionsVisible={this.state.areSuggestionsVisible}
                suggestionsLoading={this.state.suggestionsLoading}
                selectedIndex={this.state.selectedIndex}
                suggestions={this.state.suggestions}
            >
                {(props: { suggestionsVisible: boolean; suggestionsLoading: boolean; selectedIndex: number; suggestions: IIdentity[] }) => {
                    return props.suggestionsVisible ? (
                        <div className="people-picker-container" onKeyDown={this.onKeyDown}>
                            <SuggestionsList
                                {...this.props}
                                className={"WIT-suggestions-list"}
                                isLoading={props.suggestionsLoading}
                                loadingText={Resources.IdentityPickerLoadingText}
                                renderNoResultFound={this.renderNoIdentitiesFound}
                                renderSuggestion={this.renderSuggestionItem}
                                resultsMaximumNumber={resultsMaximumNumber}
                                suggestionsItemClassName={"WIT-suggestions-item"}
                                suggestions={props.suggestions}
                                selectedIndex={props.selectedIndex}
                                onSuggestionClicked={this.onSuggestionClicked}
                            />
                        </div>
                    ) : (
                            <div />
                        );
                }}
            </Observer>
        );
    }

    private renderNoIdentitiesFound = (): JSX.Element => {
        return (
            <div className="search-people-picker-no-results flex-row flex-grow flex-center">
                {Resources.IdentityPickerNoResultsText}
            </div>
        );
    };

    private renderSuggestionItem = (suggestion: IIdentitySuggestionItemProps): JSX.Element => {
        return (
            <div tabIndex={-1} className="suggestion-item-container">
                <IdentityPickerSuggestionItem {...suggestion} />
            </div>
        );
    };

    private onKeyDown = (evt: React.KeyboardEvent<HTMLElement>): void => {
        const maxResults = resultsMaximumNumber < this.state.suggestions.length ? resultsMaximumNumber : this.state.suggestions.length;
        const { selectedIndex } = this.state;
        if (evt.keyCode === KeyCode.downArrow) {
            this.setState({ selectedIndex: (selectedIndex + 1) % maxResults });
        } else if (evt.keyCode === KeyCode.upArrow) {
            const nextIndex = selectedIndex === 0 ? maxResults - 1 : selectedIndex - 1;
            this.setState({ selectedIndex: nextIndex });
        } else if (evt.keyCode === KeyCode.enter) {
            const identityName = `"${this.getIdentityName(this.state.suggestions[selectedIndex])}"`;
            const textToUpdate = getSuggestedText(this.props.searchInput.queryText(), identityName, false, false);
            this.props.searchInput.replaceText(textToUpdate);
            this.setState({
                areSuggestionsVisible: false
            });
        }

        evt.stopPropagation();
    }

    public componentDidMount = (): void => {
        this.getSuggestions(this.props.searchText);
    }

    public componentWillReceiveProps = (nextProps: IWITIdentityPickerProps): void => {
        this.getSuggestions(nextProps.searchText);
    }

    public componentWillUnmount = (): void => {
        this.timerManagement.dispose();
    }

    private getSuggestions = (searchText: string): void =>{
        if (searchText && searchText.trim() !== "") {
            this.updateSuggestionsOnType(this.pickerProvider.onFilterIdentities(searchText, []));
        } else {
            this.updateSuggestions(this.pickerProvider.onEmptyInputFocus());
        }
    }

    private updateSuggestions = (suggestions: IIdentity[] | PromiseLike<IIdentity[]>): void => {
        const suggestionsArray: IIdentity[] = suggestions as IIdentity[];
        const suggestionsPromiseLike: Promise<IIdentity[]> = suggestions as Promise<IIdentity[]>;

        if (Array.isArray(suggestionsArray)) {
            this.setState({
                suggestions: suggestionsArray,
                suggestionsLoading: false
            })
        } else if (suggestionsPromiseLike && suggestionsPromiseLike.then) {
            suggestionsPromiseLike.then((newSuggestions: IIdentity[]) => {
                this.setState({
                    suggestions: newSuggestions,
                    suggestionsLoading: false
                })
            });
        }
    };

    private onSuggestionClicked = (suggestion: IIdentitySuggestionItemProps): void => {
        const identityName = `"${this.getIdentityName(suggestion.item)}"`;
        const textToUpdate = getSuggestedText(this.props.searchInput.queryText(), identityName, false, false);
        this.props.searchInput.replaceText(textToUpdate);
        this.setState({
            areSuggestionsVisible: false,
            selectedIndex: suggestion.index
        })
    };

    private getIdentityName = (item: IIdentity): string => {
        if (!item) {
            return "";
        }

        const uniqueName = item.signInAddress || item.mail;

        if (uniqueName) {
            if (uniqueName.indexOf("@") === -1 && item.scopeName) {
                // if uniqueName is not an email, use both domain and alias
                return `${item.displayName} <${item.scopeName}\\${uniqueName}>`;
            }
            else {
                // if uniqueName is an email, only use email
                return `${item.displayName} <${uniqueName}>`;
            }
        }
        else {
            return item.displayName || "";
        }
    }
}
